function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

function blankCategory() {
  return {
    name: '',
    description: '',
    sort_type: 'manual',
    external_link: '',
    template_id: '',
    search_template_id: '',
    cms_variables: ''
  }
}

function editCategory(category) {
  if (!category) return null
  return {
    ...category,
    admin_ids_text: (category.admin_ids || []).join(','),
    involved_organization_ids_text: (category.involved_organization_ids || []).join(',')
  }
}

export default Vue.extend({
  props: {
    mode: {
      type: String,
      required: true
    },
    initialPayload: {
      type: Object,
      default() {
        return null
      }
    },
    canManageCategories: {
      type: Boolean,
      default: false
    },
    templates: {
      type: Array,
      default() {
        return []
      }
    },
    searchTemplates: {
      type: Array,
      default() {
        return []
      }
    }
  },

  data() {
    const payload = this.initialPayload || {}
    const included = payload.included || {}
    return {
      category: payload.data || null,
      children: included.children || [],
      pages: included.pages || [],
      categoryAdmins: included.category_admins || [],
      formVisible: false,
      childFormVisible: false,
      editVisible: false,
      createForm: blankCategory(),
      childForm: blankCategory(),
      roots: [],
      rootOrder: [],
      childOrder: [],
      pageOrder: [],
      editForm: editCategory(payload.data),
      moveTargets: [],
      moveTargetId: '',
      moving: false,
      templatesData: included.templates || this.templates,
      searchTemplatesData: included.search_templates || this.searchTemplates,
      userTagGroups: included.user_tag_groups || []
    }
  },

  computed: {
    authenticityToken() {
      return csrfToken()
    },

    title() {
      return this.category ? this.category.name : '所有分类'
    },

    description() {
      if (!this.category) return '管理站点的顶级分类、排序和模板配置。'
      if (this.category.external_link) return '这是一个链接分类，访问时会跳转到外部地址。'
      return this.category.description || '管理当前分类的子分类、文章和权限配置。'
    },

    categoryStateText() {
      if (!this.category) return ''
      if (this.category.external_link) return '外链分类'
      if (this.category.hidden) return '隐藏分类'
      return '普通分类'
    },

    sortTypeText() {
      if (!this.category || this.category.external_link) return ''
      return this.sortTypeLabel(this.category.sort_type)
    },

    sortTypeOptions() {
      return [
        ['手动排序', 'manual'],
        ['创建时间降序', 'created_at_desc'],
        ['创建时间升序', 'created_at_asc']
      ]
    }
  },

  created() {
    if (this.mode === 'index') this.loadRoots()
    this.resetOrders()
  },

  template: `
    <div class="categories-main-wrapper">
      <div class="categories-head content-panel">
        <div class="categories-head-main">
          <div class="categories-kicker">分类管理</div>
          <h1 class="categories-title">{{ title }}</h1>
          <p class="categories-description">{{ description }}</p>
          <div v-if="category" class="categories-meta">
            <span class="meta-chip">{{ categoryStateText }}</span>
            <span v-if="sortTypeText" class="meta-chip">{{ sortTypeText }}</span>
            <span class="meta-chip">{{ children.length }} 个子分类</span>
            <span class="meta-chip">{{ pages.length }} 篇文章</span>
          </div>
        </div>
        <div class="categories-actions">
          <a href="/admin/settings/templates" class="btn btn-default btn-small outlined mr-3">修改模板</a>
          <button v-if="mode === 'index' && canManageCategories"
                  class="btn btn-primary btn-small"
                  @click="formVisible = !formVisible">
            <i class="slp-plus"></i>
            添加分类
          </button>
          <template v-if="mode === 'show' && category">
            <a v-if="!category.external_link" :href="category.urls.new_page" class="btn btn-primary btn-small mr-3">
              <i class="slp-plus"></i>
              添加文章
            </a>
            <button class="btn btn-default btn-small outlined mr-3" @click="editVisible = !editVisible">修改</button>
            <button class="btn btn-default btn-small outlined mr-3" @click="loadMoveTargets">移动</button>
            <button class="btn btn-danger btn-small" @click="destroyCategory">删除</button>
          </template>
        </div>
      </div>

      <div class="categories-body">
        <div v-if="mode === 'index'">
          <div v-if="formVisible" class="panel-card category-form-card">
            <div class="panel-card-head">
              <span class="panel-card-title">添加分类</span>
            </div>
            <div class="panel-card-body">
              <form action="/admin/categories" method="post" class="category-form">
                <input type="hidden" name="authenticity_token" :value="authenticityToken" />
                <category-fields :model="createForm"
                                 :templates="templatesData"
                                 :search-templates="searchTemplatesData"
                                 :sort-type-options="sortTypeOptions"
                                 :include-search-template="true" />
                <div class="form-actions">
                  <button type="button" class="btn btn-default outlined" @click="formVisible = false">取消</button>
                  <button type="submit" class="btn btn-success" data-disable-with="添加中...">添加</button>
                </div>
              </form>
            </div>
          </div>

          <div class="panel-card categories-list-card">
            <div class="panel-card-head">
              <span class="panel-card-title">顶级分类</span>
              <div class="panel-card-actions" v-if="canManageCategories && roots.length > 1">
                <button class="btn btn-default btn-small outlined" @click="saveRootOrder">保存排序</button>
              </div>
            </div>
            <div class="panel-card-body">
              <category-list :items="roots"
                             :order.sync="rootOrder"
                             empty-text="暂无分类"
                             @visit="visitCategory" />
            </div>
          </div>
        </div>

        <div v-else-if="category">
          <div v-if="editVisible" class="panel-card category-form-card">
            <div class="panel-card-head">
              <span class="panel-card-title">修改分类</span>
            </div>
            <div class="panel-card-body">
              <form :action="category.urls.update" method="post" class="category-form">
                <input type="hidden" name="authenticity_token" :value="authenticityToken" />
                <input type="hidden" name="_method" value="patch" />
                <category-fields :model="editForm"
                                 :templates="templatesData"
                                 :search-templates="searchTemplatesData"
                                 :sort-type-options="sortTypeOptions"
                                 :external-category="!!category.external_link"
                                 :user-tag-groups="userTagGroups"
                                 :include-permissions="true"
                                 :can-manage-admins="category.permissions && category.permissions.manage_admins" />
                <div class="form-actions">
                  <button type="button" class="btn btn-default outlined" @click="editVisible = false">取消</button>
                  <button type="submit" class="btn btn-success" data-disable-with="保存中...">保存修改</button>
                </div>
              </form>
            </div>
          </div>

          <div v-if="moveTargets.length > 0 || moving" class="panel-card category-form-card">
            <div class="panel-card-head">
              <span class="panel-card-title">移动分类</span>
            </div>
            <div class="panel-card-body">
              <select v-model="moveTargetId" class="controlled-input">
                <option value="">选择要移动到的分类</option>
                <option v-for="target in moveTargets" :key="target.id" :value="target.id">{{ target.name }}</option>
              </select>
              <div class="form-actions">
                <button class="btn btn-default outlined" :disabled="moving" @click="moveTargets = []">取消</button>
                <button class="btn btn-success" :disabled="moving || !moveTargetId" @click="moveCategory(false)">移动</button>
                <button v-if="!category.root" class="btn btn-success" :disabled="moving" @click="moveCategory(true)">移到顶级</button>
              </div>
            </div>
          </div>

          <div v-if="category.external_link" class="panel-card">
            <div class="panel-card-head">
              <span class="panel-card-title">链接分类</span>
            </div>
            <div class="panel-card-body">
              <table class="table table-noborder break-word-table info-table">
                <tbody>
                  <tr>
                    <th>链接地址</th>
                    <td><a :href="category.external_link" target="_blank">{{ category.external_link }}</a></td>
                  </tr>
                  <tr>
                    <th>说明</th>
                    <td class="text-muted">由于该分类有链接，不能创建下级分类和文章。</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <template v-else>
            <div v-if="childFormVisible" class="panel-card category-form-card">
              <div class="panel-card-head">
                <span class="panel-card-title">添加子分类</span>
              </div>
              <div class="panel-card-body">
                <form action="/admin/categories" method="post" class="category-form">
                  <input type="hidden" name="authenticity_token" :value="authenticityToken" />
                  <input type="hidden" name="cms_category[parent_id]" :value="category.id" />
                  <category-fields :model="childForm"
                                   :templates="templatesData"
                                   :search-templates="searchTemplatesData"
                                   :sort-type-options="sortTypeOptions"
                                   :include-search-template="false" />
                  <div class="form-actions">
                    <button type="button" class="btn btn-default outlined" @click="childFormVisible = false">取消</button>
                    <button type="submit" class="btn btn-success" data-disable-with="添加中...">添加</button>
                  </div>
                </form>
              </div>
            </div>

            <div class="panel-card categories-list-card">
              <div class="panel-card-head">
                <span class="panel-card-title">子分类</span>
                <div class="panel-card-actions">
                  <button class="btn btn-primary btn-small mr-3" @click="childFormVisible = !childFormVisible">
                    <i class="slp-plus"></i>
                    添加子分类
                  </button>
                  <button v-if="children.length > 1" class="btn btn-default btn-small outlined" @click="saveChildOrder">保存排序</button>
                </div>
              </div>
              <div class="panel-card-body">
                <category-list :items="children"
                               :order.sync="childOrder"
                               empty-text="暂无子分类"
                               @visit="visitCategory" />
              </div>
            </div>

            <div class="panel-card categories-list-card">
              <div class="panel-card-head">
                <span class="panel-card-title">文章</span>
                <div class="panel-card-actions">
                  <span class="mr-3">{{ pages.length }} 篇文章</span>
                  <span v-if="!category.manual" class="badge">{{ sortTypeLabel(category.sort_type) }}</span>
                  <button v-else-if="pages.length > 1" class="btn btn-default btn-small outlined" @click="savePageOrder">保存排序</button>
                </div>
              </div>
              <div class="panel-card-body">
                <category-list :items="pages"
                               :order.sync="pageOrder"
                               empty-text="暂无文章"
                               item-type="page"
                               @visit="visitPage" />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  `,

  components: {
    'category-fields': {
      props: {
        model: Object,
        templates: Array,
        searchTemplates: Array,
        sortTypeOptions: Array,
        externalCategory: Boolean,
        includeSearchTemplate: Boolean,
        includePermissions: Boolean,
        userTagGroups: {
          type: Array,
          default() {
            return []
          }
        },
        canManageAdmins: Boolean
      },

      computed: {
        involvedOrganizationIds() {
          return String(this.model.involved_organization_ids_text || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        }
      },

      template: `
        <div>
          <div class="form-row">
            <label>名称</label>
            <input class="controlled-input" name="cms_category[name]" v-model="model.name" required />
          </div>
          <div class="form-row">
            <label>描述</label>
            <textarea class="controlled-input" name="cms_category[description]" v-model="model.description"></textarea>
          </div>
          <div class="form-row" v-if="!externalCategory">
            <label>排序方式</label>
            <select class="controlled-input" name="cms_category[sort_type]" v-model="model.sort_type">
              <option v-for="option in sortTypeOptions" :key="option[1]" :value="option[1]">{{ option[0] }}</option>
            </select>
          </div>
          <div class="form-row">
            <label>外部链接</label>
            <input class="controlled-input" name="cms_category[external_link]" v-model="model.external_link" />
          </div>
          <div class="form-row">
            <label>图标</label>
            <input class="controlled-input" name="cms_category[icon]" v-model="model.icon" />
          </div>
          <div class="form-row">
            <label>隐藏</label>
            <label class="checkbox-row">
              <input type="hidden" name="cms_category[hidden]" value="false" />
              <input type="checkbox" name="cms_category[hidden]" value="true" v-model="model.hidden" />
              是
            </label>
          </div>
          <div class="form-row" v-if="!externalCategory">
            <label>分类模板</label>
            <select class="controlled-input" name="cms_category[template_id]" v-model="model.template_id">
              <option value=""></option>
              <option v-for="template in templates" :key="template.id" :value="template.id">{{ template.name }}</option>
            </select>
          </div>
          <div class="form-row" v-if="includeSearchTemplate && !externalCategory">
            <label>搜索模板</label>
            <select class="controlled-input" name="cms_category[search_template_id]" v-model="model.search_template_id">
              <option value=""></option>
              <option v-for="template in searchTemplates" :key="template.id" :value="template.id">{{ template.name }}</option>
            </select>
          </div>
          <div class="form-row">
            <label>CMS 变量</label>
            <textarea class="controlled-input" name="cms_category[cms_variables]" v-model="model.cms_variables"></textarea>
          </div>
          <template v-if="includePermissions">
            <div class="form-row">
              <label>关联用户标签</label>
              <input type="hidden" name="cms_category[involved_user_tag_ids][]" value="" />
              <select class="controlled-input" name="cms_category[involved_user_tag_ids][]" v-model="model.involved_user_tag_ids" multiple>
                <optgroup v-for="group in userTagGroups" :key="group.id" :label="group.name">
                  <option v-for="tag in group.tags" :key="tag.id" :value="tag.id">{{ tag.name }}</option>
                </optgroup>
              </select>
            </div>
            <div class="form-row">
              <label>关联组织 ID</label>
              <input class="controlled-input" v-model="model.involved_organization_ids_text" />
              <input type="hidden" name="cms_category[involved_organization_ids][]" value="" />
              <input v-for="id in involvedOrganizationIds"
                     :key="id"
                     type="hidden"
                     name="cms_category[involved_organization_ids][]"
                     :value="id" />
            </div>
            <div class="form-row">
              <label>管理员 ID</label>
              <input class="controlled-input" name="cms_category[admin_ids]" v-model="model.admin_ids_text" :disabled="!canManageAdmins" />
            </div>
          </template>
        </div>
      `
    },

    'category-list': {
      props: {
        items: Array,
        order: Array,
        emptyText: String,
        itemType: {
          type: String,
          default: 'category'
        }
      },

      computed: {
        orderedItems() {
          const map = {}
          this.items.forEach((item) => { map[item.id] = item })
          return this.order.map((id) => map[id]).filter(Boolean)
        }
      },

      template: `
        <div>
          <div v-if="orderedItems.length === 0" class="no-data">{{ emptyText }}</div>
          <div v-else class="categories-list-table">
            <div v-for="(item, index) in orderedItems" :key="item.id" class="category-row">
              <div class="category-row-sort">
                <button class="btn icon-btn link-btn squared" :disabled="index === 0" @click="move(index, -1)">
                  <i class="fa fa-chevron-up"></i>
                </button>
                <button class="btn icon-btn link-btn squared" :disabled="index === orderedItems.length - 1" @click="move(index, 1)">
                  <i class="fa fa-chevron-down"></i>
                </button>
              </div>
              <div class="category-row-main" @click="$emit('visit', item)">
                <span class="category-row-icon">
                  <i v-if="item.hidden" class="fa fa-eye-slash"></i>
                  <i v-else-if="item.icon" :class="'fa ' + item.icon"></i>
                  <i v-else-if="itemType === 'page'" class="fa fa-file-text-o"></i>
                  <i v-else class="fa fa-folder-o"></i>
                </span>
                <span class="category-row-copy">
                  <span class="category-row-title">{{ item.name || item.title }}</span>
                  <span class="category-row-subtitle">
                    <template v-if="itemType === 'page'">文章</template>
                    <template v-else-if="item.external_link">外链分类</template>
                    <template v-else-if="item.hidden">隐藏分类</template>
                    <template v-else>分类</template>
                  </span>
                </span>
                <span v-if="item.external_link" class="badge badge-primary">外链</span>
              </div>
            </div>
          </div>
        </div>
      `,

      methods: {
        move(index, offset) {
          const next = this.order.slice()
          const target = index + offset
          const item = next[index]
          next.splice(index, 1)
          next.splice(target, 0, item)
          this.$emit('update:order', next)
        }
      }
    }
  },

  methods: {
    loadRoots() {
      Skylark.Libs.Ajax.get('/admin/categories.json', {
        responseType: 'json',
        success: (categories) => {
          this.roots = categories
          this.rootOrder = categories.map((category) => category.id)
        }
      })
    },

    resetOrders() {
      this.childOrder = this.children.map((category) => category.id)
      this.pageOrder = this.pages.map((page) => page.id)
    },

    visitCategory(category) {
      Turbolinks.visit(this.categoryPath(category))
    },

    visitPage(page) {
      Turbolinks.visit(page.urls.show)
    },

    categoryPath(category) {
      return category.urls && category.urls.show ? category.urls.show : `/admin/categories/${category.id}`
    },

    sortTypeLabel(sortType) {
      const option = this.sortTypeOptions.find((item) => item[1] === sortType)
      return option ? option[0] : sortType
    },

    saveRootOrder() {
      this.patchOrder('/admin/categories/sort_roots.json', this.rootOrder)
    },

    saveChildOrder() {
      this.patchOrder(this.category.urls.sort, this.childOrder)
    },

    savePageOrder() {
      this.patchOrder(this.category.urls.pages_sort, this.pageOrder)
    },

    patchOrder(url, positions) {
      $.ajax({
        url,
        type: 'patch',
        data: { positions },
        success: (data, status, jqxhr) => {
          Turbolinks.visit(jqxhr.getResponseHeader('Location') || location.href)
        },
        error: () => {
          Skylark.Bridge.newAlertify('排序保存失败', { type: 'danger' })
        }
      })
    },

    loadMoveTargets() {
      Skylark.Libs.Ajax.get(this.category.urls.targets_moved_to, {
        responseType: 'json',
        success: (categories) => {
          this.moveTargets = categories
          this.moveTargetId = categories[0] ? categories[0].id : ''
        }
      })
    },

    moveCategory(toRoot) {
      this.moving = true
      const data = toRoot ? { to_root: true } : { target_id: this.moveTargetId }
      $.ajax({
        url: this.category.urls.move,
        type: 'patch',
        data,
        success: () => {
          Turbolinks.visit(this.category.urls.show)
        },
        error: () => {
          Skylark.Bridge.newAlertify('移动失败', { type: 'danger' })
        },
        complete: () => {
          this.moving = false
        }
      })
    },

    destroyCategory() {
      if (!window.confirm(`确定删除 ${this.category.name} 分类？`)) return
      const form = document.createElement('form')
      form.method = 'post'
      form.action = this.category.urls.destroy
      form.innerHTML = `
        <input type="hidden" name="authenticity_token" value="${this.authenticityToken}">
        <input type="hidden" name="_method" value="delete">
      `
      document.body.appendChild(form)
      form.submit()
    }
  }
})
